<template>
  <div class="iq-card iq-card-sm">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h5 class="mb-0">{{ $t('pos.lbl_catalog') }}</h5>
        <ul class="nav nav-pills" role="tablist">
          <li class="nav-item" role="presentation">
            <button type="button" role="tab" :aria-selected="tab === 'services'" :class="['nav-link', 'pos-tap', tab === 'services' ? 'active' : '']" @click="selectTab('services')">
              {{ $t('pos.lbl_services') }} <span class="badge bg-secondary ms-1">{{ store.catalog.services.length }}</span>
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button type="button" role="tab" :aria-selected="tab === 'products'" :class="['nav-link', 'pos-tap', tab === 'products' ? 'active' : '']" @click="selectTab('products')">
              {{ $t('pos.lbl_products') }} <span class="badge bg-secondary ms-1">{{ store.catalog.products.length }}</span>
            </button>
          </li>
        </ul>
      </div>

      <div class="d-flex align-items-center gap-2 mb-3">
        <div class="pos-search flex-grow-1">
          <i class="fa-solid pos-search-icon" :class="scanMode ? 'fa-barcode' : 'fa-magnifying-glass'" aria-hidden="true"></i>
          <input
            ref="searchInput"
            type="search"
            class="form-control"
            :class="{ 'pos-search-scan': scanMode }"
            :placeholder="scanMode ? $t('pos.lbl_scan_ph') : $t('pos.lbl_filter_ph')"
            v-model="filter"
            :aria-label="scanMode ? $t('pos.lbl_scan_ph') : $t('pos.lbl_filter_ph')"
            @keyup.enter="onFilterEnter"
          />
        </div>
        <!-- Modo lectura: escaneo continuo por SKU/codigo (autofoco + re-foco). -->
        <button
          type="button"
          class="btn pos-scan-btn pos-tap"
          :class="scanMode ? 'btn-primary' : 'btn-outline-secondary'"
          :aria-pressed="scanMode"
          :title="$t('pos.lbl_scan_mode')"
          @click="toggleScan"
        >
          <i class="fa-solid fa-barcode me-1" aria-hidden="true"></i>{{ $t('pos.lbl_scan_mode') }}
        </button>
      </div>

      <!-- Feedback del ultimo escaneo. -->
      <div class="pos-scan-feedback mb-3" v-if="scanMode && lastScanned">
        <i class="fa-solid fa-circle-check me-1" aria-hidden="true"></i>{{ $t('pos.lbl_scan_added') }}: <strong>{{ lastScanned }}</strong>
      </div>

      <!-- Filtro por categoria (chips). Se deriva del catalogo cargado; el chip
           activo restringe la grid. "Todas" resetea. En productos, ademas, un
           chip "Con descuento" para ver solo lo rebajado (como "En Descuento"). -->
      <div class="pos-cats mb-3" v-if="!store.loadingCatalog && (activeCategories.length || tab === 'products')">
        <button type="button" class="pos-cat pos-tap" :class="{ active: selectedCategoryId === null && !onlyDiscount }" @click="clearCategory">
          {{ $t('pos.lbl_all_categories') }}
        </button>
        <button
          v-for="cat in activeCategories"
          :key="`cat-${cat.id}`"
          type="button"
          class="pos-cat pos-tap"
          :class="{ active: selectedCategoryId === cat.id }"
          @click="selectCategory(cat.id)"
        >
          {{ cat.name }} <span class="pos-cat-count">{{ cat.count }}</span>
        </button>
        <button
          v-if="tab === 'products' && hasDiscountedProducts"
          type="button"
          class="pos-cat pos-cat-discount pos-tap"
          :class="{ active: onlyDiscount }"
          @click="toggleDiscount"
        >
          <i class="fa-solid fa-tag me-1" aria-hidden="true"></i>{{ $t('pos.lbl_on_discount') }}
        </button>
      </div>

      <div v-if="store.loadingCatalog" class="text-center py-4 text-muted">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{{ $t('pos.lbl_loading') }}
      </div>

      <!-- Servicios -->
      <div v-else-if="tab === 'services'" class="pos-grid">
        <p class="text-muted mb-0" v-if="!filteredServices.length">{{ $t('pos.lbl_no_services') }}</p>
        <button
          v-for="service in filteredServices"
          :key="`service-${service.id}`"
          type="button"
          class="pos-tile pos-tap"
          @click="store.addService(service)"
        >
          <span class="pos-tile-media">
            <img v-if="service.image" :src="service.image" :alt="service.name" loading="lazy" />
            <span v-else class="pos-tile-avatar" :style="avatarStyle(service.name)">{{ initial(service.name) }}</span>
          </span>
          <span class="pos-tile-name">{{ service.name }}</span>
          <span class="pos-tile-meta">{{ service.duration_min }} {{ $t('pos.lbl_min') }}</span>
          <span class="pos-tile-price">{{ money(service.price) }}</span>
        </button>
      </div>

      <!-- Productos -->
      <div v-else class="pos-grid">
        <p class="text-muted mb-0" v-if="!filteredProducts.length">{{ $t('pos.lbl_no_products') }}</p>
        <template v-for="product in filteredProducts" :key="`product-${product.id}`">
          <button
            v-for="variation in product.variations"
            :key="`variation-${variation.product_variation_id}`"
            type="button"
            class="pos-tile pos-tap"
            :class="{ 'pos-tile-disabled': variation.stock < 1 }"
            :disabled="variation.stock < 1"
            @click="store.addProduct(product, variation)"
          >
            <span class="pos-tile-badge" v-if="variation.discount > 0"><i class="fa-solid fa-tag" aria-hidden="true"></i></span>
            <span class="pos-tile-media">
              <img v-if="product.image" :src="product.image" :alt="product.name" loading="lazy" />
              <span v-else class="pos-tile-avatar" :style="avatarStyle(product.name)">{{ initial(product.name) }}</span>
            </span>
            <span class="pos-tile-name">{{ product.name }}</span>
            <span class="pos-tile-meta" v-if="product.has_variation && variation.name">{{ variation.name }}</span>
            <span class="pos-tile-price">
              <template v-if="variation.discount > 0">
                <s class="text-muted me-1">{{ money(variation.price) }}</s>{{ money(variation.net_price) }}
              </template>
              <template v-else>{{ money(variation.price) }}</template>
            </span>
            <span class="pos-tile-stock" :class="{ 'text-danger': variation.stock < 1 }">
              {{ variation.stock < 1 ? $t('pos.lbl_out_of_stock') : `${$t('pos.lbl_stock')}: ${variation.stock}` }}
            </span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { usePos } from '../../store/pos'
import { money } from '../money'

const store = usePos()
const tab = ref('services')
const filter = ref('')

// Categoria activa del filtro (null = todas) + filtro "solo con descuento".
const selectedCategoryId = ref(null)
const onlyDiscount = ref(false)

// --- Modo lectura de codigo de barras (escaneo continuo) ---
const scanMode = ref(false)
const searchInput = ref(null)
const lastScanned = ref('')

const toggleScan = async () => {
  scanMode.value = !scanMode.value
  lastScanned.value = ''
  if (scanMode.value) {
    await nextTick()
    searchInput.value?.focus()
  }
}

// --- Avatar de respaldo cuando un item no tiene imagen ---
const initial = (name) => (name || '?').trim().charAt(0).toUpperCase()

/** Color estable a partir del nombre (mismo item -> mismo color). */
const avatarStyle = (name) => {
  let hash = 0
  const text = name || ''
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash)
  const hue = Math.abs(hash) % 360
  return { background: `hsl(${hue}, 55%, 92%)`, color: `hsl(${hue}, 45%, 38%)` }
}

const matches = (text) => (text || '').toLowerCase().includes(filter.value.trim().toLowerCase())

/** Una variacion esta rebajada si el catalogo le calculo un descuento vigente. */
const productHasDiscount = (product) => product.variations.some((variation) => (Number(variation.discount) || 0) > 0)

const hasDiscountedProducts = computed(() => store.catalog.products.some(productHasDiscount))

/**
 * Categorias del tab activo, con conteo, deducidas del catalogo cargado. En
 * servicios cada uno tiene 1 categoria (category_id + category_name); en
 * productos son N (array `categories`). Se ordenan por nombre para una barra
 * estable.
 */
const activeCategories = computed(() => {
  const map = new Map()

  if (tab.value === 'services') {
    store.catalog.services.forEach((service) => {
      if (!service.category_id || !service.category_name) return
      const entry = map.get(service.category_id) || { id: service.category_id, name: service.category_name, count: 0 }
      entry.count += 1
      map.set(service.category_id, entry)
    })
  } else {
    store.catalog.products.forEach((product) => {
      ;(product.categories || []).forEach((category) => {
        if (!category || !category.id) return
        const entry = map.get(category.id) || { id: category.id, name: category.name, count: 0 }
        entry.count += 1
        map.set(category.id, entry)
      })
    })
  }

  return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

const serviceInCategory = (service) => selectedCategoryId.value === null || service.category_id === selectedCategoryId.value

const productInCategory = (product) =>
  selectedCategoryId.value === null || (product.categories || []).some((category) => category.id === selectedCategoryId.value)

const filteredServices = computed(() =>
  store.catalog.services.filter((service) => matches(service.name) && serviceInCategory(service))
)

/**
 * Se filtra por nombre de producto o de variacion, por categoria y por el filtro
 * de descuento; se ocultan los productos cuyas variaciones no casan (no tiene
 * sentido pintar una tarjeta vacia).
 */
const filteredProducts = computed(() =>
  store.catalog.products
    .filter((product) => productInCategory(product) && (!onlyDiscount.value || productHasDiscount(product)))
    .map((product) => {
      if (matches(product.name)) return product
      const variations = product.variations.filter((variation) => matches(variation.name) || matches(variation.sku))
      return variations.length ? { ...product, variations } : null
    })
    .filter(Boolean)
)

const selectTab = (value) => {
  tab.value = value
  clearCategory()
}

const clearCategory = () => {
  selectedCategoryId.value = null
  onlyDiscount.value = false
}

const selectCategory = (id) => {
  selectedCategoryId.value = selectedCategoryId.value === id ? null : id
  onlyDiscount.value = false
}

const toggleDiscount = () => {
  onlyDiscount.value = !onlyDiscount.value
}

/**
 * Enter en el buscador = anadir el UNICO match (patron de lector de codigo de
 * barras: escanea -> reduce a 1 -> anade y limpia). Prioriza coincidencia exacta
 * de SKU/codigo. Sin match unico, no hace nada (deja el filtro tal cual).
 */
const afterScan = async (name) => {
  filter.value = ''
  // En modo lectura se anuncia lo anadido y se re-enfoca para el siguiente escaneo.
  if (scanMode.value) {
    lastScanned.value = name
    await nextTick()
    searchInput.value?.focus()
  }
}

const onFilterEnter = () => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return

  if (tab.value === 'services') {
    const list = filteredServices.value
    const exact = list.find((s) => (s.name || '').toLowerCase() === q)
    const pick = exact || (list.length === 1 ? list[0] : null)
    if (pick) {
      store.addService(pick)
      afterScan(pick.name)
    }
    return
  }

  // Productos: se aplana a pares {product, variation} para casar por SKU/codigo.
  const pairs = []
  filteredProducts.value.forEach((product) => {
    product.variations.forEach((variation) => pairs.push({ product, variation }))
  })

  const exact = pairs.find((p) => (p.variation.sku || '').toLowerCase() === q || (p.variation.name || '').toLowerCase() === q)
  const pick = exact || (pairs.length === 1 ? pairs[0] : null)

  if (pick && (Number(pick.variation.stock) || 0) >= 1) {
    store.addProduct(pick.product, pick.variation)
    afterScan(pick.product.name)
  }
}
</script>
